// Renders one tool call row with its input and output.

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { TruncatedText } from '@/components/shared/truncated-text';
import { cn } from '@/lib/utils';
import { toolCallStatus, toolFieldTree, type ToolField } from './tool-call-format';
import type { LabelledValueDto, TraceStep } from './types';

/** Payload rows shown before the rest folds behind "Show more". */
const VISIBLE_ROWS = 5;

const STATUS_BADGE = {
  worked: { label: 'Worked', className: 'bg-green-100 text-green-500' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-500' },
  waiting: { label: 'Waiting', className: 'bg-amber-50 text-amber-500' },
  hidden: { label: 'Cannot display', className: 'bg-gray-100 text-gray-800' },
} as const;

function PayloadRow({ row }: { row: LabelledValueDto }) {
  return (
    // Right-aligned like FieldRow: two flex-1 columns stranded the value mid-row.
    <div className="flex w-full items-start justify-between gap-2">
      {/* Capped so a long key wraps instead of pushing its value out of the box. */}
      <p className="min-w-0 max-w-1/2 shrink-0 wrap-break-word text-[12px] font-medium leading-4 text-gray-900">{row.label}</p>
      <TruncatedText
        text={row.value}
        copyable
        className="min-w-0 text-right text-[12px] leading-4 text-text-body"
      />
    </div>
  );
}

/** A nested object or list; open by default, folded by its header. */
function PayloadGroup({ label, fields }: { label: string; fields: ToolField[] }) {
  const [open, setOpen] = useState(true);

  return (
    <div className="flex w-full flex-col gap-1">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex w-fit max-w-full cursor-pointer items-center gap-1 text-left text-[12px] font-medium leading-4 text-gray-900"
      >
        <span className="min-w-0 wrap-break-word">{label}</span>
        <ChevronDown className={cn('size-3 shrink-0 transition-transform duration-200 motion-reduce:transition-none', open && 'rotate-180')} />
      </button>
      {open && (
        <div className="flex w-full flex-col gap-1 border-l border-gray-400 pl-2">
          <PayloadFields fields={fields} />
        </div>
      )}
    </div>
  );
}

function PayloadFields({ fields }: { fields: ToolField[] }) {
  // Siblings can repeat a label (two lists of the same key), so the position keeps keys unique.
  return fields.map((field, index) =>
    field.kind === 'group' ? (
      <PayloadGroup key={`${index}:${field.label}`} label={field.label} fields={field.children} />
    ) : (
      <PayloadRow key={`${index}:${field.label}`} row={field} />
    ),
  );
}

export function PayloadSection({ title, payload }: { title: string; payload: unknown }) {
  const [showAll, setShowAll] = useState(false);
  const rows = toolFieldTree(payload);
  const visible = showAll ? rows : rows.slice(0, VISIBLE_ROWS);

  return (
    <div className="flex w-full flex-col gap-1">
      <p className="w-full font-mono text-[11px] font-medium leading-4 text-gray-800 underline decoration-dotted decoration-[1.3px] underline-offset-4">
          {title}
        </p>
      <div className="relative flex w-full flex-col gap-1 rounded-[10px] bg-gray-100 p-1.5">
        {rows.length > 0 ? (
          <PayloadFields fields={visible} />
        ) : (
          <p className="text-[12px] leading-4 text-gray-700">No details</p>
        )}
        {rows.length > VISIBLE_ROWS && (
          <>
            {/* Fades the last rows out so the fold reads as "there is more below". */}
            {!showAll && (
              <div
                aria-hidden
                className="pointer-events-none absolute inset-x-0 bottom-9 h-12 bg-gradient-to-t from-gray-100 via-gray-100/70 to-transparent backdrop-blur-[1.5px] [mask-image:linear-gradient(to_top,black,transparent)]"
              />
            )}
            <button
              type="button"
              onClick={() => setShowAll((prev) => !prev)}
              className="z-10 flex w-fit cursor-pointer items-center gap-1 self-center rounded-lg border border-gray-400 bg-white px-2 py-1 text-[12px] leading-4 text-text-body"
            >
              {showAll ? 'Show less' : `Show ${rows.length - VISIBLE_ROWS} more`}
              <ChevronDown className={cn('size-3 transition-transform duration-200 motion-reduce:transition-none', showAll && 'rotate-180')} />
            </button>
          </>
        )}
      </div>
    </div>
  );
}

export function ToolCallCard({ step }: { step: TraceStep }) {
  const [open, setOpen] = useState(true);
  const status = toolCallStatus(step);
  const badge = STATUS_BADGE[status];
  // A failed call shows only which tool broke — its payloads are noise to the agent.
  const failed = status === 'failed';
  const expandable = !failed && (step.toolInput !== undefined || step.toolOutput !== undefined);

  return (
    <div className="flex w-full flex-col px-3">
      <div className="flex w-full items-center justify-between gap-2 py-2">
        <button
          type="button"
          onClick={() => expandable && setOpen((prev) => !prev)}
          disabled={!expandable}
          className="flex min-w-0 cursor-pointer items-center gap-1 text-left disabled:cursor-default"
          aria-expanded={expandable ? open : undefined}
          aria-label={expandable ? (open ? `Collapse ${step.title}` : `Expand ${step.title}`) : undefined}
        >
          <TruncatedText text={step.title} sideOffset={-2} className="text-[13px] font-medium leading-[18px] text-text-body" />
          {expandable && <ChevronDown
              className={cn('size-4 shrink-0 transition-transform duration-200 motion-reduce:transition-none', open && 'rotate-180')}
            />}
        </button>
        <span className={cn('shrink-0 rounded-2xl px-2.5 py-1 text-[12px] leading-4', badge.className)}>
          {badge.label}
        </span>
      </div>

      {failed && step.toolName && (
        <div className="flex w-full flex-col gap-1 pb-2.5 pt-0.5">
          <PayloadRow row={{ label: 'Tool', value: step.toolName }} />
        </div>
      )}

      {open && expandable && (
        <div className="flex w-full flex-col gap-3 pb-2.5 pt-0.5 animate-in fade-in-0 duration-200 ease-out motion-reduce:animate-none">
          {step.toolInput !== undefined && <PayloadSection title="INPUT" payload={step.toolInput} />}
          {step.toolOutput !== undefined && <PayloadSection title="OUTPUT" payload={step.toolOutput} />}
        </div>
      )}
    </div>
  );
}
